"use client";
import * as Toolbar from "@radix-ui/react-toolbar";
import { useMemo } from "react";
import { useEditor } from "novel";
import { useStorage } from "@/lib/storage/provider";
import SettingsButton from "@/components/tailwind/ui/settings-button";
import { Bold, Italic, Underline, Heading1, Heading2, Heading3, List, ListOrdered, Code, TextQuote } from "lucide-react";

function ItemButton({
  pressed,
  onClick,
  disabled,
  children,
  title,
}: {
  pressed?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <Toolbar.Button
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      title={title}
      className={
        "inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors " +
        (pressed ? "bg-purple-100 text-purple-600" : "text-muted-foreground hover:bg-accent")
      }
    >
      {children}
    </Toolbar.Button>
  );
}

export default function EditorToolbar({
  saveStatus,
  words,
  showFilesButton = true,
  onToggleFiles,
  showImportExport = true,
  showSettings = true,
}: {
  saveStatus: string;
  words: number;
  showFilesButton?: boolean;
  onToggleFiles?: () => void;
  showImportExport?: boolean;
  showSettings?: boolean;
}) {
  const { editor } = useEditor();
  const { adapter } = useStorage();
  const canMarkdown = !!editor?.storage?.markdown?.parser && !!editor?.storage?.markdown?.serializer;

  const statusText = useMemo(() => {
    if (saveStatus === "Unsaved") return "Saving…";
    try {
      const ts = Number(window.localStorage.getItem("lastSavedAt") || 0);
      if (!ts) return "Saved";
      const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
      if (mins === 0) return "Saved just now";
      if (mins === 1) return "Edited 1 minute ago";
      return `Edited ${mins} minutes ago`;
    } catch {
      return saveStatus;
    }
  }, [saveStatus]);

  return (
    <Toolbar.Root
      aria-label="Editor toolbar"
      className="w-full max-w-screen-lg mx-auto rounded-lg border border-neutral-200 bg-background/80 supports-[backdrop-filter]:bg-background/60 backdrop-blur shadow-sm px-3 py-2 flex flex-col gap-2"
    >
      {/* Row 1: Files / Import / Export | Status | Settings */}
      <div className="flex items-center gap-2">
        {showFilesButton ? (
          <Toolbar.Button
            className="inline-flex h-9 items-center justify-center gap-2 rounded-md border px-3 text-sm hover:bg-accent"
            onClick={onToggleFiles}
            title="Files"
          >
            Files
          </Toolbar.Button>
        ) : null}
        {showImportExport && (
          <>
            <Toolbar.Button
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md border px-3 text-sm hover:bg-accent disabled:opacity-60"
              disabled={!canMarkdown}
              onClick={() => canMarkdown && triggerImport(editor)}
              title="Import Markdown"
            >
              Import
            </Toolbar.Button>
            <Toolbar.Button
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md border px-3 text-sm hover:bg-accent disabled:opacity-60"
              disabled={!canMarkdown}
              onClick={() => canMarkdown && triggerExport(editor, adapter)}
              title="Export Markdown"
            >
              Export
            </Toolbar.Button>
          </>
        )}
        <div className="mx-2 h-6 w-px bg-neutral-300" />
        <div className="text-xs sm:text-sm text-muted-foreground">
          {statusText}
          {words ? ` • ${words} words` : null}
        </div>
        <div className="grow" />
        {showSettings && (
          <div className="px-1">
            <SettingsButton />
          </div>
        )}
      </div>

      {/* Row 2: Formatting controls */}
      <div className="flex flex-wrap items-center gap-1">
        <ItemButton
          title="Bold"
          pressed={editor?.isActive("bold")}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          disabled={!editor?.can().chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </ItemButton>
        <ItemButton
          title="Italic"
          pressed={editor?.isActive("italic")}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          disabled={!editor?.can().chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </ItemButton>
        <ItemButton
          title="Underline"
          pressed={editor?.isActive("underline")}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          disabled={!editor?.can().chain().focus().toggleUnderline().run()}
        >
          <Underline className="h-4 w-4" />
        </ItemButton>

        <div className="mx-1 h-5 w-px bg-neutral-300" />

        <ItemButton
          title="Heading 1"
          pressed={editor?.isActive("heading", { level: 1 })}
          onClick={() => editor?.chain().focus().setNode("heading", { level: 1 }).run()}
        >
          <Heading1 className="h-4 w-4" />
        </ItemButton>
        <ItemButton
          title="Heading 2"
          pressed={editor?.isActive("heading", { level: 2 })}
          onClick={() => editor?.chain().focus().setNode("heading", { level: 2 }).run()}
        >
          <Heading2 className="h-4 w-4" />
        </ItemButton>
        <ItemButton
          title="Heading 3"
          pressed={editor?.isActive("heading", { level: 3 })}
          onClick={() => editor?.chain().focus().setNode("heading", { level: 3 }).run()}
        >
          <Heading3 className="h-4 w-4" />
        </ItemButton>

        <div className="mx-1 h-5 w-px bg-neutral-300" />

        <ItemButton
          title="Bullet List"
          pressed={editor?.isActive("bulletList")}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <List className="h-4 w-4" />
        </ItemButton>
        <ItemButton
          title="Numbered List"
          pressed={editor?.isActive("orderedList")}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-4 w-4" />
        </ItemButton>

        <div className="mx-1 h-5 w-px bg-neutral-300" />

        <ItemButton
          title="Quote"
          pressed={editor?.isActive("blockquote")}
          onClick={() => editor?.chain().focus().toggleBlockquote().run()}
        >
          <TextQuote className="h-4 w-4" />
        </ItemButton>
        <ItemButton
          title="Code Block"
          pressed={editor?.isActive("codeBlock")}
          onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
        >
          <Code className="h-4 w-4" />
        </ItemButton>
      </div>
    </Toolbar.Root>
  );
}

function triggerImport(editor: any) {
  if (!editor) return;
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".md,text/markdown";
  input.onchange = () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const reader = new FileReader();
      reader.onerror = () => console.error("Import failed", reader.error);
      reader.onload = () => {
        const text = String(reader.result || "");
        try {
          const md = editor?.storage?.markdown;
          if (!md?.parser) return;
          const node = md.parser.parse(text);
          editor.chain().focus().setContent(node.toJSON(), false).run();
        } catch (e) {
          console.error("Import failed", e);
        }
      };
      reader.readAsText(file);
    } catch (e) {
      console.error("Import failed", e);
    }
  };
  input.click();
}

async function triggerExport(editor: any, adapter: any) {
  if (!editor) return;
  try {
    const hasLocal = hasLocalAssets(editor.getJSON());
    let mode: ExportMode = "inline";
    // If local assets exist, try remote links first; else optionally offer zip bundle
    if (hasLocal) {
      // First attempt remote upload mode silently; fallback to prompt
      const tryRemote = await serializeWithAssets(editor, adapter, "remote");
      if (tryRemote.ok) {
        return downloadMarkdown(tryRemote.markdown, extractTitle(editor));
      }
      const bundle = window.confirm(
        "This document contains local images. Export as a .zip with separate asset files? (OK = zip bundle, Cancel = inline as data URLs)",
      );
      mode = bundle ? "zip" : "inline";
    }
    const res = await serializeWithAssets(editor, adapter, mode);
    if (res.ok && res.mode === "zip" && res.zipBlob) {
      return downloadBlob(res.zipBlob, `${extractTitle(editor) || "document"}.zip`);
    }
    const md = res.ok ? res.markdown : editor.storage.markdown.getMarkdown();
    const title = extractTitle(editor) || "document";
    downloadMarkdown(md, title);
  } catch (e) {
    console.error("Export failed", e);
  }
}

function extractTitle(editor: any): string {
  try {
    const md: string = editor.storage.markdown.getMarkdown();
    const first = (md.split(/\r?\n/).find((l: string) => l.trim().length) ?? "Untitled").trim();
    return first.replace(/^#+\s*/, "").slice(0, 140) || "Untitled";
  } catch {
    return "Untitled";
  }
}

type ExportMode = "inline" | "zip" | "remote";

async function serializeWithAssets(
  editor: any,
  adapter: any,
  mode: ExportMode = "inline",
): Promise<{ ok: true; mode: ExportMode; markdown: string; zipBlob?: Blob } | { ok: false; mode: ExportMode }> {
  try {
    const json = editor.getJSON();
    const assets: Record<string, { blob: Blob; ext: string } | { url: string }> = {};
    async function mapNode(node: any): Promise<any> {
      if (!node) return node;
      if (Array.isArray(node)) return Promise.all(node.map(mapNode));
      if (typeof node !== "object") return node;
      const out: any = { ...node };
      if (out.type === "image" && typeof out.attrs?.src === "string") {
        const src: string = out.attrs.src;
        if (src.startsWith("ploom://asset/")) {
          const key = src.replace("ploom://asset/", "");
          try {
            const blob = await adapter.getAsset(key);
            if (blob) {
              if (mode === "remote") {
                const remote = await tryRemoteUpload(blob, `asset-${key}`, blob.type);
                if (remote) {
                  out.attrs = { ...out.attrs, src: remote };
                } else {
                  // fallback to inline if remote fails; caller may try zip/inline next
                  out.attrs = { ...out.attrs, src: await blobToDataURL(blob) };
                }
              } else if (mode === "zip") {
                const ext = mimeToExt(blob.type);
                assets[key] = { blob, ext } as any;
                out.attrs = { ...out.attrs, src: `assets/${key}${ext}` };
              } else {
                out.attrs = { ...out.attrs, src: await blobToDataURL(blob) };
              }
            }
          } catch {}
        }
      }
      if (Array.isArray(out.content)) out.content = await Promise.all(out.content.map(mapNode));
      return out;
    }
    const transformed = await mapNode(json);
    const schema = editor.view.state.schema;
    const doc = schema.nodeFromJSON(transformed);
    const markdown = editor.storage.markdown.serializer.serialize(doc);

    if (mode === "zip") {
      try {
        const JSZip = (await import(/* @vite-ignore */ "jszip")).default as any;
        const zip = new JSZip();
        zip.file("document.md", markdown);
        const folder = zip.folder("assets");
        for (const [key, val] of Object.entries(assets)) {
          if ((val as any).blob) {
            const { blob, ext } = val as any;
            folder.file(`${key}${ext}`, blob);
          }
        }
        const zipBlob = await zip.generateAsync({ type: "blob" });
        return { ok: true, mode, markdown, zipBlob };
      } catch (e) {
        console.warn("JSZip not available; falling back to inline export.");
        // fallback to inline build
        return await serializeWithAssets(editor, adapter, "inline");
      }
    }

    return { ok: true, mode, markdown };
  } catch (e) {
    console.error(e);
    return { ok: false, mode };
  }
}

function hasLocalAssets(json: any): boolean {
  let found = false;
  const walk = (n: any) => {
    if (!n || found) return;
    if (Array.isArray(n)) return n.forEach(walk);
    if (typeof n !== "object") return;
    if (n.type === "image" && typeof n.attrs?.src === "string" && n.attrs.src.startsWith("ploom://asset/")) found = true;
    if (Array.isArray(n.content)) n.content.forEach(walk);
  };
  walk(json);
  return found;
}

async function tryRemoteUpload(blob: Blob, name: string, mime: string): Promise<string | null> {
  try {
    const res = await fetch("/api/upload", {
      method: "POST",
      headers: { "content-type": mime || "application/octet-stream", "x-vercel-filename": name },
      body: blob,
    });
    if (res.status === 200) {
      const { url } = await res.json();
      return url as string;
    }
    return null;
  } catch {
    return null;
  }
}

function mimeToExt(mime: string): string {
  const map: Record<string, string> = {
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "image/jpg": ".jpg",
    "image/gif": ".gif",
    "image/webp": ".webp",
    "image/svg+xml": ".svg",
  };
  return map[mime] || "";
}

function downloadMarkdown(md: string, title: string) {
  const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
  downloadBlob(blob, `${title}.md`);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error);
    fr.onload = () => resolve(String(fr.result));
    fr.readAsDataURL(blob);
  });
}
